import React, { useLayoutEffect } from 'react';
import styles from './DescriptionTab.module.css';
import { FaShareAlt, FaFilePdf, FaTimes } from 'react-icons/fa';
import { createPortal } from 'react-dom';
import Image from 'next/image';
import Link from 'next/link';
import { normalizeImageUrl } from '@/utils/image';
import ConsultationBanner from './ConsultationBanner';
import RegionalDataBanner from './RegionalDataBanner';
import SharePopup from './SharePopup';

interface DescriptionTabProps {
    blocks?: any[];
    onSectionVisible?: (sectionId: string) => void;
    reportId?: string;
    contentId?: string;
    reportTitle?: string;
    slug?: string;
    stickyOffset?: number;
    graphAlt?: string;
    graphTitle?: string;
}

const DescriptionTab: React.FC<DescriptionTabProps> = ({
    blocks,
    onSectionVisible,
    reportId,
    contentId,
    reportTitle,
    slug,
    stickyOffset = 158, // Fallback
    graphAlt,
    graphTitle
}) => {
    const [isShareOpen, setIsShareOpen] = React.useState(false);
    const [currentInfographicUrl, setCurrentInfographicUrl] = React.useState('');
    const [zoomImageUrl, setZoomImageUrl] = React.useState<string | null>(null);
    const [mounted, setMounted] = React.useState(false);
    const [isZoomLoading, setIsZoomLoading] = React.useState(true);

    const handleZoomClick = (url: string) => {
        setIsZoomLoading(true);
        setZoomImageUrl(url);
    };

    React.useEffect(() => {
        setMounted(true);
        return () => setMounted(false);
    }, []);

    // Scroll freeze
    React.useEffect(() => {
        if (zoomImageUrl && mounted) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [zoomImageUrl, mounted]);

    const [dynamicTotalOffset, setDynamicTotalOffset] = React.useState(stickyOffset + 60);

    // Compute the real offset synchronously before paint so that scrollMarginTop
    // is correct on the very first render (fixes heading cutoff for sections 1 & 2).
    // Use the tabs bar's actual bottom Y coordinate — the same value used by
    // scrollToSection — so both scroll targets and scroll-spy thresholds agree.
    useLayoutEffect(() => {
        const tabsBar = document.querySelector('[class*="tabHeaderList"]');
        const tabsBarBottom = tabsBar
            ? tabsBar.getBoundingClientRect().bottom
            : stickyOffset + 60;
        setDynamicTotalOffset(tabsBarBottom + 12);
    }, [stickyOffset]);

    // Escape key listener to close zoomed image preview
    React.useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                setZoomImageUrl(null);
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, []);

    // ... existing observer code and share code ...
    const observerRefs = React.useRef<{ [key: string]: HTMLDivElement | null }>({});

    // Find description block
    const descriptionBlock = blocks?.find(b => b.block_type === 'description');
    const rawSections = descriptionBlock?.block_data_json?.sections || [];
    const reportType = descriptionBlock?.block_data_json?.report_type;

    // Padding added to section wrapper divs so the heading is always below
    // the sticky tabs bar when a section becomes active.
    const SECTION_PADDING = 12;

    React.useEffect(() => {
        if (!onSectionVisible) return;

        // Scroll-based scroll-spy — works correctly in BOTH directions (up & down).
        //
        // Algorithm: among all sections, find the one whose HEADING (section.top +
        // SECTION_PADDING) is closest to the threshold from above. This means:
        //  • The section heading has just entered the visible content area.
        //  • The heading is always visible when the section is highlighted.
        //  • We don't rely on Object.keys() insertion order (no `break`).
        const handleScroll = () => {
            const refs = observerRefs.current;
            // Threshold: heading must be at or below this Y to count as "in view".
            // dynamicTotalOffset = tabsBarBottom + 24, so heading threshold = tabsBarBottom + 24.
            const offset = dynamicTotalOffset;

            let activeId = '';
            let firstSectionId = '';
            let bestTop = -Infinity; // largest section.top that still satisfies condition

            for (const id of Object.keys(refs)) {
                const el = refs[id];
                if (!el) continue;
                if (!firstSectionId) firstSectionId = id;

                const top = el.getBoundingClientRect().top;
                // Condition: the heading (at section.top + SECTION_PADDING) has
                // crossed the threshold line.
                if (top + SECTION_PADDING <= offset && top > bestTop) {
                    bestTop = top;
                    activeId = id;
                }
            }

            // Fallback: if no heading has crossed the threshold yet (user is at
            // the very top), highlight the first section.
            if (!activeId && firstSectionId) {
                activeId = firstSectionId;
            }

            if (activeId) {
                onSectionVisible(activeId);
            }
        };

        window.addEventListener('scroll', handleScroll, { passive: true });
        handleScroll(); // run immediately on mount / tab-switch

        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, [onSectionVisible, rawSections, dynamicTotalOffset]);

    const handleShare = (url: string) => {
        setCurrentInfographicUrl(url);
        setIsShareOpen(true);
    };

    // ✅ Show fallback message if no description sections
    if (rawSections.length === 0) {
        return (
            <div className={styles.descriptionContent}>
                <div className={styles.noDescription}>
                    No description available
                </div>
            </div>
        );
    }

    const renderGridSectionContent = (htmlContent: string) => {
        const lines = htmlContent.split('\n');
        const headingLines: string[] = [];
        const introLines: string[] = [];
        const bulletLines: string[] = [];
        const outroLines: string[] = [];

        let inBullets = false;
        let hasPassedBullets = false;

        lines.forEach(line => {
            const trimmed = line.trim();
            if (!trimmed) return;

            // Skip list wrapper tags (ul) so they do not render as empty blocks
            if (/^\s*<\/?ul>/i.test(trimmed)) {
                return;
            }

            // Match if line starts with bullet symbol (optionally after HTML tags) or list item tag
            const isBullet = /^\s*(?:<[^>]+>)*\s*[•\-\*–—]/i.test(trimmed) || /^\s*(?:<[^>]+>)*\s*<li/i.test(trimmed);

            if (isBullet) {
                bulletLines.push(line);
                inBullets = true;
            } else if (trimmed.toLowerCase().includes('<h2') || trimmed.toLowerCase().includes('</h2')) {
                headingLines.push(line);
            } else {
                if (inBullets) {
                    hasPassedBullets = true;
                    inBullets = false;
                }
                if (hasPassedBullets) {
                    outroLines.push(line);
                } else {
                    introLines.push(line);
                }
            }
        });

        const headingHtml = headingLines.join('\n').trim();
        const introHtml = introLines.join('\n').trim();
        const outroHtml = outroLines.join('\n').trim();

        return (
            <div>
                {headingHtml && (
                    <div
                        className={styles.htmlContent}
                        dangerouslySetInnerHTML={{ __html: headingHtml.replace(/(<\/h2>)\s+/gi, '$1') }}
                    />
                )}
                {introHtml && (
                    <div
                        className={styles.htmlContent}
                        dangerouslySetInnerHTML={{ __html: introHtml }}
                    />
                )}
                {bulletLines.length > 0 && (
                    <div className={styles.bulletsGrid}>
                        {bulletLines.map((bullet, idx) => (
                            <div
                                key={idx}
                                className={styles.bulletGridItem}
                                dangerouslySetInnerHTML={{ __html: bullet.trim() }}
                            />
                        ))}
                    </div>
                )}
                {outroHtml && (
                    <div
                        className={styles.htmlContent}
                        dangerouslySetInnerHTML={{ __html: outroHtml }}
                    />
                )}
            </div>
        );
    };

    // The sheet column is named "Market Size Gain Million N", but the content team supplies
    // values with the unit baked in ("$11.09 Billion") as well as bare ones ("$ 866").
    // The backend passes non-numeric cells through untouched, so appending a hardcoded
    // "Million" produced "+ $11.09 Billion Million". Only append the unit when the value
    // does not already carry one.
    const formatGain = (gain: any) => {
        const raw = String(gain ?? '').replace('$ ', '$').trim();
        if (!raw) return '';
        return /\b(thousand|million|billion|trillion)\b/i.test(raw) ? raw : `${raw} Million`;
    };

    const renderCards = (cards: any[]) => {
        return (
            <div
                className={styles.marketGainCardsContainer}
                style={{ '--mg-cols': Math.min(cards.length, 3) } as React.CSSProperties}
            >
                {cards.map((card: any, idx: number) => (
                    <div key={idx} className={styles.marketGainCard}>
                        <div className={styles.cardIconWrapper}>
                            <svg viewBox="0 0 24 24" className={styles.cardIcon} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <rect x="3" y="18" width="3" height="4" rx="0.5"></rect>
                                <rect x="9" y="14" width="3" height="8" rx="0.5"></rect>
                                <rect x="15" y="10" width="3" height="12" rx="0.5"></rect>
                                <polyline points="3 9.5 9 3.5 13 7.5 21 0.5"></polyline>
                                <polyline points="17 0.5 21 0.5 21 4.5"></polyline>
                            </svg>
                        </div>
                        <div className={styles.cardContent}>
                            <div className={styles.cardTitle}>{card.segment}</div>
                            <div className={styles.cardSubtitle}>{card.type}</div>
                            <div className={styles.cardValue}>+ {formatGain(card.gain)}</div>
                        </div>
                    </div>
                ))}
            </div>
        );
    };

    const renderSectionContent = (section: any) => {
        switch (section.type) {
            case 'table': {
                const { rows = [], columns = [] } = section.content || {};
                const customizationUrl = `/Customise?id=${reportId}&name=${encodeURIComponent(reportTitle || '')}&type=smp`;
                const purchaseOptionsUrl = `/purchaseoptions?id=${reportId}&slug=${slug}`;

                return (
                    <div style={{ overflowX: 'auto' }}>
                        <table className={styles.dataTable}>
                            <thead>
                                <tr>
                                    {columns.map((col: string, i: number) => (
                                        <th key={i}>{col}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((row: any, rIndex: number) => (
                                    <tr key={rIndex}>
                                        {columns.map((col: string, cIndex: number) => (
                                            <td key={cIndex} style={cIndex === 0 ? { fontWeight: 700 } : {}}>
                                                {row[col] ?? row['Details'] ?? '-'}
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                                <tr>
                                    <td style={{ fontWeight: 700 }}>Customization Scope</td>
                                    <td colSpan={columns.length - 1}>
                                        <Link href={customizationUrl} className={styles.tableLink}>Request for Customization</Link>
                                    </td>
                                </tr>
                                <tr>
                                    <td style={{ fontWeight: 700 }}>Pricing And Purchase Options</td>
                                    <td colSpan={columns.length - 1}>
                                        <Link href={purchaseOptionsUrl} className={styles.tableLink}>Explore Purchase Options</Link>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                );
            }

            // Fallback for all other types (paragraph, bullets, grouped_bullets, etc.) to use HTML handling
            // This covers the new requirement where content is returned as HTML string
            default:
                // Check if content is a string (HTML)
                if (typeof section.content === 'string') {
                    const topicLower = section.topic_name?.toLowerCase();
                    const isGridSection = topicLower === 'key players market shares' ||
                        topicLower === 'regional market shares';

                    if (isGridSection) {
                        return renderGridSectionContent(section.content);
                    }

                    // Remove extra whitespace/newlines immediately after closing h2 tag
                    // This prevents 'white-space: pre-line' from rendering a huge gap due to \n\n
                    const cleanedContent = section.content.replace(/(<\/h2>)\s+/gi, '$1');

                    return (
                        <div
                            className={styles.htmlContent}
                            dangerouslySetInnerHTML={{ __html: cleanedContent }}
                        />
                    );
                }
                // Legacy support for structured content if strictly needed, 
                // but user implies everything except table is now HTML string
                // We'll trust the default HTML rendering for strings.

                // If it's old structure (array), we might not render it correctly with simple dangerous HTML
                // But the requirement says "now u will get in html format"
                return null;
        }
    };

    const renderSection = (section: any, index: number) => {
        const infographic = section.infographic;
        const sectionId = section.topic_name ? section.topic_name.toLowerCase().replace(/\s+/g, '-') : `section-${index}`;
        const showShare = section.topic_name?.toLowerCase() === 'historic market size';

        const sectionContent = (
            <>
                {section.heading && (
                    <h3 className={`${styles.subTitle} ${section.type === 'table' ? styles.centerTitle : ''}`}>
                        {section.heading}
                    </h3>
                )}
                {renderSectionContent(section)}
            </>
        );

        // ── Determine infographic mode ───────────────────────────────────────
        const isBelow = (infographic?.enabled && infographic?.position === 'below' && Array.isArray(infographic?.images)) ||
                        (infographic?.enabled && infographic?.type === 'cards' && Array.isArray(infographic?.cards));
        // OLD format: has flat url (no position key) — render beside
        const isBeside = infographic?.enabled && !infographic?.position && !!infographic?.url;

        // ── Render below images (1 or 2) ────────────────────────────────────
        const renderBelowImages = () => {
            const validImages = (infographic.images as any[]).filter((img: any) => !!img?.url);
            if (validImages.length === 0) return null;

            const isMajorPlayers = section.topic_name?.toLowerCase() === 'major players';
            const isMarketValue = section.topic_name?.toLowerCase() === 'market value definition';

            if (validImages.length === 1) {
                const img = validImages[0];
                const caption = isMajorPlayers
                    ? (img.url.toLowerCase().includes('scoring') || img.url.toLowerCase().includes('matrix')
                        ? "This chart maps companies by product innovation and brand strength, with bubble size indicating relative revenue, helping identify market leaders, challengers, and niche players. This is an illustrative chart; the full report provides a complete and accurate competitive analysis."
                        : "This chart is for illustrative purposes; the full report includes a detailed competitor analysis and comprehensive overview of the top 10 companies in the market.")
                    : isMarketValue
                        ? (img.url.toLowerCase().includes('attractiveness')
                            ? "This chart presents market attractiveness based on a quantitative evaluation of growth, competition, strategic alignment, and risk, offering a clear view of opportunity areas for decision-making. This chart is for illustrative purposes; the full report contains the complete analysis."
                            : "This chart highlights the Total Addressable Market (TAM) by estimating the maximum revenue opportunity using an assumption-driven approach, supporting strategic planning and opportunity sizing across markets. The chart is illustrative; the full report provides a more comprehensive analysis.")
                        : (img.alt_text || "");

                return (
                    <div className={styles.belowImageWrapper}>
                        <div className={styles.belowImageItem}>
                            <Image
                                src={normalizeImageUrl(img.url)}
                                alt={img.alt_text || section.topic_name || 'Market Infographic'}
                                title={img.title || section.topic_name || 'Market Infographic'}
                                width={900}
                                height={500}
                                className={`${styles.belowImage} ${styles.zoomableImage}`}
                                style={{ width: '100%', height: 'auto' }}
                                onClick={() => handleZoomClick(normalizeImageUrl(img.url))}
                            />
                            {caption && (
                                <p className={styles.belowImageCaption}>
                                    {caption}
                                </p>
                            )}
                            {showShare && (
                                <button
                                    className={styles.shareButton}
                                    onClick={() => handleShare(normalizeImageUrl(img.url))}
                                    title="Share this infographic"
                                >
                                    <FaShareAlt className={styles.shareIcon} /> Share
                                </button>
                            )}
                        </div>
                    </div>
                );
            }

            // 2 images side by side
            return (
                <div className={styles.belowImageGrid}>
                    {validImages.map((img: any, i: number) => {
                        const caption = isMajorPlayers
                            ? (img.url.toLowerCase().includes('scoring') || img.url.toLowerCase().includes('matrix')
                                ? "This chart maps companies by product innovation and brand strength, with bubble size indicating relative revenue, helping identify market leaders, challengers, and niche players. This is an illustrative chart; the full report provides a complete and accurate competitive analysis."
                                : "This chart is for illustrative purposes; the full report includes a detailed competitor analysis and comprehensive overview of the top 10 companies in the market.")
                            : isMarketValue
                                ? (img.url.toLowerCase().includes('attractiveness')
                                    ? "This chart presents market attractiveness based on a quantitative evaluation of growth, competition, strategic alignment, and risk, offering a clear view of opportunity areas for decision-making. This chart is for illustrative purposes; the full report contains the complete analysis."
                                    : "This chart highlights the Total Addressable Market (TAM) by estimating the maximum revenue opportunity using an assumption-driven approach, supporting strategic planning and opportunity sizing across markets. The chart is illustrative; the full report provides a more comprehensive analysis.")
                                : (img.alt_text || "");

                        return (
                            <div key={i} className={styles.belowImageItem}>
                                <Image
                                    src={normalizeImageUrl(img.url)}
                                    alt={img.alt_text || section.topic_name || 'Market Infographic'}
                                    title={img.title || section.topic_name || 'Market Infographic'}
                                    width={600}
                                    height={400}
                                    className={`${styles.belowImage} ${styles.zoomableImage}`}
                                    style={{ width: '100%', height: 'auto' }}
                                    onClick={() => handleZoomClick(normalizeImageUrl(img.url))}
                                />
                                {caption && (
                                    <p className={styles.belowImageCaption}>
                                        {caption}
                                    </p>
                                )}
                                {showShare && (
                                    <button
                                        className={styles.shareButton}
                                        onClick={() => handleShare(normalizeImageUrl(img.url))}
                                        title="Share this infographic"
                                    >
                                        <FaShareAlt className={styles.shareIcon} /> Share
                                    </button>
                                )}
                            </div>
                        );
                    })}
                </div>
            );
        };

        // ── Build final layout ───────────────────────────────────────────────
        let content: React.ReactNode;

        if (isBeside) {
            const isRegionalOutlook = section.topic_name?.toLowerCase() === 'regional outlook';
            const isHistoricMarketSize = section.topic_name?.toLowerCase() === 'historic market size';
            const hasImage = !!infographic.url;
            const requestDataUrl = `/regional?id=${reportId}&name=${encodeURIComponent(reportTitle || '')}&type=smp`;

            content = (
                <div>
                    <div className={styles.sectionWithInfographic}>
                        <div className={styles.infographicWrapper}>
                            <Image
                                src={normalizeImageUrl(infographic.url)}
                                alt={isHistoricMarketSize ? (graphAlt || infographic.alt_text || section.heading || 'Market Infographic') : (infographic.alt_text || section.heading || 'Market Infographic')}
                                title={isHistoricMarketSize ? (graphTitle || infographic.title || '') : (infographic.title || infographic.alt_text || '')}
                                width={800}
                                height={450}
                                className={`${styles.infographicImage} ${styles.zoomableImage}`}
                                style={{ width: '100%', height: 'auto' }}
                                onClick={() => handleZoomClick(normalizeImageUrl(infographic.url))}
                                priority
                            />
                            {showShare && (
                                <button
                                    className={styles.shareButton}
                                    onClick={() => handleShare(normalizeImageUrl(infographic.url))}
                                    title="Share this infographic"
                                >
                                    <FaShareAlt className={styles.shareIcon} /> Share
                                </button>
                            )}
                        </div>
                        <div className={styles.sectionTextContent}>
                            {sectionContent}
                        </div>
                    </div>
                    {isRegionalOutlook && hasImage && (
                        <div className={styles.regionalRequestContainer}>
                            <span className={styles.regionalRequestText}>
                                Need data for a specific geography?
                            </span>
                            <Link href={requestDataUrl} className={styles.regionalRequestButton}>
                                <FaFilePdf className={styles.pdfIcon} /> Request Regional Data
                            </Link>
                        </div>
                    )}
                </div>
            );
        } else if (isBelow) {
            const isMarketGain = section.topic_name === 'Market Gain';

            if (isMarketGain) {
                const validImages = Array.isArray(infographic?.images) ? (infographic.images as any[]).filter((img: any) => !!img?.url) : [];
                const firstImg = validImages[0];
                const MARKET_GAIN_DEFAULT_CAPTION = 'Market Gain identifies the most promising market opportunities by highlighting the segments or products expected to generate the highest incremental revenue growth over the next five years.';
                const caption = (firstImg?.title && firstImg.title.trim()) || MARKET_GAIN_DEFAULT_CAPTION;

                // Split the HTML at the first </h2> so heading renders above the image
                // and everything after it renders below the caption
                const rawHtml = typeof section.content === 'string'
                    ? section.content.replace(/(<\/h2>)\s+/gi, '$1')
                    : '';
                const H2_CLOSE = '</h2>';
                const h2End = rawHtml.indexOf(H2_CLOSE);

                let headingHtml = '';
                let bodyHtml = rawHtml;

                if (h2End !== -1) {
                    headingHtml = rawHtml.slice(0, h2End + H2_CLOSE.length);
                    bodyHtml = rawHtml.slice(h2End + H2_CLOSE.length).trimStart();
                }

                content = (
                    <div>
                        {/* 1. h2 heading — above the image */}
                        {headingHtml && (
                            <div
                                className={styles.htmlContent}
                                dangerouslySetInnerHTML={{ __html: headingHtml }}
                            />
                        )}
                        {/* 2. Cards or Image */}
                        {infographic.type === 'cards' && Array.isArray(infographic.cards) ? (
                            renderCards(infographic.cards)
                        ) : (
                            renderBelowImages()
                        )}
                        {/* 3. Italic caption below image */}
                        {caption && (
                            <p className={styles.marketGainCaption}>{caption}</p>
                        )}
                        {/* 4. Rest of content text */}
                        {bodyHtml && (
                            <div
                                className={styles.htmlContent}
                                dangerouslySetInnerHTML={{ __html: bodyHtml }}
                            />
                        )}
                    </div>
                );
            } else if (section.topic_name?.toLowerCase() === 'major drivers') {
                const validImages = (infographic?.images as any[])?.filter((img: any) => !!img?.url) || [];
                const hasImage = validImages.length > 0;
                const sampleRequestUrl = `/sample_request?id=${reportId}&type=smp&name=${encodeURIComponent(reportTitle || '')}`;

                content = (
                    <div>
                        {sectionContent}
                        {renderBelowImages()}
                        {hasImage && (
                            <>
                                <p className={styles.majorDriversCaption}>
                                    The chart presents an impact analysis of key drivers and restraints, quantifying their relative influence on the market's growth rate and helping assess the balance between growth enablers and limiting factors. This chart offers a high-level perspective; the full report contains more detailed insights.
                                </p>
                                <div className={styles.downloadSampleContainer}>
                                    <span className={styles.downloadSampleText}>
                                        Download the sample report for a detailed analysis
                                    </span>
                                    <Link href={sampleRequestUrl} className={styles.downloadSampleButton}>
                                        <FaFilePdf className={styles.pdfIcon} /> Download Sample
                                    </Link>
                                </div>
                            </>
                        )}
                    </div>
                );
            } else if (section.topic_name?.toLowerCase() === 'market value definition') {
                const validImages = (infographic?.images as any[])?.filter((img: any) => !!img?.url) || [];
                const hasImage = validImages.length > 0;
                const purchaseOptionsUrl = `/purchaseoptions?id=${reportId}&slug=${slug}`;

                content = (
                    <div>
                        {sectionContent}
                        {renderBelowImages()}
                        {hasImage && (
                            <div className={styles.marketValueCtaContainer}>
                                <span className={styles.marketValueCtaText}>
                                    Access full TAM calculations and growth projections in our report
                                </span>
                                <Link href={purchaseOptionsUrl} className={styles.marketValueCtaButton}>
                                    <FaFilePdf className={styles.pdfIcon} /> Get Report
                                </Link>
                            </div>
                        )}
                    </div>
                );
            } else if (section.topic_name?.toLowerCase() === 'major players' || section.topic_name?.toLowerCase() === 'major_players') {
                const validImages = (infographic?.images as any[])?.filter((img: any) => !!img?.url) || [];
                const hasImage = validImages.length > 0;
                const purchaseOptionsUrl = `/purchaseoptions?id=${reportId}&slug=${slug}`;
                const isNonEpr = reportType?.toLowerCase() === 'non-epr';

                content = (
                    <div>
                        {sectionContent}
                        {renderBelowImages()}
                        {hasImage && isNonEpr && (
                            <div className={styles.majorPlayersCtaContainer}>
                                <span className={styles.majorPlayersCtaText}>
                                    Explore how leading companies compare across key metrics in our report
                                </span>
                                <Link href={purchaseOptionsUrl} className={styles.majorPlayersCtaButton}>
                                    <FaFilePdf className={styles.pdfIcon} /> Get Report
                                </Link>
                            </div>
                        )}
                    </div>
                );
            } else {
                // All other below sections: text first, image(s) below
                content = (
                    <div>
                        {sectionContent}
                        {renderBelowImages()}
                    </div>
                );
            }
        } else {
            // No infographic
            content = <div>{sectionContent}</div>;
        }

        return (
            <div
                key={index}
                id={sectionId}
                ref={el => { observerRefs.current[sectionId] = el; }}
                style={{
                    scrollMarginTop: `${dynamicTotalOffset}px`,
                    // paddingTop prevents CSS margin-collapse through the wrapper and
                    // ensures the heading is always SECTION_PADDING px below the
                    // sticky tabs bar when this section becomes the active one.
                    paddingTop: `${SECTION_PADDING}px`,
                }}
            >
                {content}
            </div>
        );
    };

    const author = descriptionBlock?.block_data_json?.author;
    const nonTableSections = rawSections.filter((s: any) => s.type !== 'table');
    const tableSections = rawSections.filter((s: any) => s.type === 'table');

    // Indices for banners
    // Find the section with "market segmentation" — check both heading and topic_name
    const marketSegmentationIndex = nonTableSections.findIndex((s: any) => {
        const heading = (s.heading || '').toLowerCase();
        const topic = (s.topic_name || '').toLowerCase();
        return (
            heading.includes('market segmentation') ||
            heading.includes('market segment') ||
            topic.includes('market segmentation') ||
            topic.includes('market segment')
        );
    });

    let consultationBannerIndex = -1;
    let positionBefore = false;

    if (marketSegmentationIndex !== -1) {
        consultationBannerIndex = marketSegmentationIndex;
        positionBefore = true; // Position ConsultationBanner ABOVE (before) the market segmentation section
    } else {
        // Fallback to forecast section (position AFTER)
        const forecastSectionIndex = nonTableSections.findIndex((s: any) => {
            const heading = (s.heading || '').toLowerCase();
            const topic = (s.topic_name || '').toLowerCase();
            return (
                heading.includes('forecast') ||
                heading.includes('growth forecast') ||
                topic.includes('forecast') ||
                topic.includes('growth forecast')
            );
        });
        consultationBannerIndex = forecastSectionIndex !== -1 ? forecastSectionIndex : 0;
        positionBefore = false;
    }

    // Regional Data Banner after 70% of non-table content
    const regionalBannerIndex = Math.floor(nonTableSections.length * 0.7);

    return (
        <div className={styles.descriptionContent}>
            <SharePopup
                isOpen={isShareOpen}
                onClose={() => setIsShareOpen(false)}
                shareUrl={currentInfographicUrl}
                title={reportTitle || 'Market Infographic'}
                citation={rawSections.find((s: any) => s.infographic?.url && normalizeImageUrl(s.infographic.url) === currentInfographicUrl)?.infographic?.citation || ""}
            />
            {nonTableSections.map((s: any, i: number) => (
                <React.Fragment key={i}>
                    {positionBefore && i === consultationBannerIndex && <ConsultationBanner />}
                    {renderSection(s, i)}
                    {!positionBefore && i === consultationBannerIndex && <ConsultationBanner />}
                    {i === regionalBannerIndex && (i !== consultationBannerIndex || positionBefore) &&
                        !['epr', 'non-epr'].includes((reportType || '').toLowerCase()) && (
                        <RegionalDataBanner reportId={reportId} reportTitle={reportTitle} />
                    )}
                </React.Fragment>
            ))}
            {author && (
                <div className={styles.authorDisplay}>
                    Author : <span>{author}</span>
                </div>
            )}
            <div
                id="market-snapshot"
                style={{ scrollMarginTop: `${dynamicTotalOffset}px` }}
                ref={el => { observerRefs.current['market-snapshot'] = el; }}
            >
                {tableSections.map((s: any, i: number) => renderSection(s, i + nonTableSections.length))}
            </div>
            {mounted && zoomImageUrl && createPortal(
                <div className={styles.zoomOverlay} onClick={() => setZoomImageUrl(null)}>
                    <div className={styles.zoomModal} onClick={(e) => e.stopPropagation()}>
                        <button className={styles.zoomCloseButton} onClick={() => setZoomImageUrl(null)} title="Close preview">
                            <FaTimes />
                        </button>
                        {isZoomLoading && (
                            <div className={styles.zoomSpinner}>
                                <div className={styles.spinner}></div>
                            </div>
                        )}
                        <img 
                            src={zoomImageUrl} 
                            alt="Zoomed Infographic" 
                            className={`${styles.zoomImage} ${isZoomLoading ? styles.hiddenImage : ''}`} 
                            onLoad={() => setIsZoomLoading(false)}
                        />
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
};

export default DescriptionTab;
